import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AddBankMethodRequest,
  AddCardMethodRequest,
  AffiliateTransactionDto,
  AnomalyAlertsDto,
  ApiPosOrderDto,
  AssetDto,
  BalancePointDto,
  BalanceReportDto,
  BankAccountDto,
  BusinessAccountDto,
  CalculateWithdrawRequest,
  CardDto,
  CreateBankAccountRequest,
  CreateBusinessAccountRequest,
  BuyCryptoRequest,
  CreateInvoiceRequest,
  CurrencyDto,
  PagedResult,
  ShopOrderDetailDto,
  CreateShopRequest,
  UpdateShopRequest,
  CreateStandingOrderRequest,
  CreateUserRequest,
  OrderCardRequest,
  CreateWalletAddressRequest,
  DashboardStatsDto,
  DepositAddressDto,
  DepositCurrencyDto,
  FeeDto,
  FiatWithdrawRequest,
  KycBusinessDetailsRequest,
  KycDocumentsRequest,
  KycIdDetailsRequest,
  InternalTransferRequest,
  InternalTransferResultDto,
  KycStatusDto,
  CreateLimitOrderRequest,
  LimitOrderDto,
  MarketIndicesDto,
  MerchantLookupDto,
  NotificationDto,
  NotificationFeedDto,
  NotificationPreferences,
  PaymentMethodDto,
  PerformanceDto,
  ProfileDto,
  PushConfigDto,
  PushSubscribeRequest,
  RateRequest,
  RateResultDto,
  SellCryptoRequest,
  ShopDto,
  ShopOrderDto,
  StandingOrderDto,
  StandingOrderRunResultDto,
  TradeResultDto,
  TransactionPageDto,
  UnreadCountDto,
  UpdateProfileRequest,
  UpdateStandingOrderRequest,
  RoleDto,
  UserDto,
  WalletAddressDto,
  WalletSummaryDto,
  WithdrawCalcDto,
  WithdrawCryptoRequest,
  WithdrawResultDto
} from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class KlidiApi {
  private http = inject(HttpClient);
  private base = environment.apiBaseUrl;

  /**
   * GETs a file through HttpClient so the interceptor attaches the bearer token, observing the
   * full response so callers can honour the server's Content-Disposition filename.
   *
   * Every authenticated download must go through here. Handing an API URL to `window.open` or
   * an `<a href>` bypasses the interceptor and the API answers UNAUTHENTICATED.
   */
  private downloadBlob(url: string): Observable<HttpResponse<Blob>> {
    return this.http.get(url, { observe: 'response', responseType: 'blob' });
  }

  walletSummary(merchantId: number): Observable<WalletSummaryDto> {
    return this.http.get<WalletSummaryDto>(`${this.base}/wallet/summary`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  /**
   * Daily portfolio value, oldest first, with today computed live. The series starts at the first
   * captured snapshot — there is no history before the backend began recording it.
   */
  balanceHistory(merchantId: number, days: number): Observable<BalancePointDto[]> {
    return this.http.get<BalancePointDto[]>(`${this.base}/wallet/balance-history`, {
      params: new HttpParams().set('merchantId', merchantId).set('days', days)
    });
  }

  fiatAssets(merchantId: number): Observable<AssetDto[]> {
    return this.http.get<AssetDto[]>(`${this.base}/wallet/assets/fiat`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  cryptoAssets(merchantId: number): Observable<AssetDto[]> {
    return this.http.get<AssetDto[]>(`${this.base}/wallet/assets/crypto`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  transactions(merchantId: number, page = 1, pageSize = 25): Observable<TransactionPageDto> {
    return this.http.get<TransactionPageDto>(`${this.base}/transactions`, {
      params: new HttpParams()
        .set('merchantId', merchantId)
        .set('page', page)
        .set('pageSize', pageSize)
    });
  }

  dashboardStats(merchantId: number): Observable<DashboardStatsDto> {
    return this.http.get<DashboardStatsDto>(`${this.base}/dashboard/stats`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  bankAccounts(merchantId: number): Observable<BankAccountDto[]> {
    return this.http.get<BankAccountDto[]>(`${this.base}/banking/ibans`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  addBankAccount(merchantId: number, req: CreateBankAccountRequest): Observable<BankAccountDto> {
    return this.http.post<BankAccountDto>(`${this.base}/banking/ibans`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  cryptoWallets(merchantId: number): Observable<WalletAddressDto[]> {
    return this.http.get<WalletAddressDto[]>(`${this.base}/crypto-wallets`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  whitelist(merchantId: number): Observable<WalletAddressDto[]> {
    return this.http.get<WalletAddressDto[]>(`${this.base}/crypto-wallets/whitelist`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  addWhitelist(merchantId: number, req: CreateWalletAddressRequest): Observable<WalletAddressDto> {
    return this.http.post<WalletAddressDto>(`${this.base}/crypto-wallets/whitelist`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  profile(merchantId: number): Observable<ProfileDto> {
    return this.http.get<ProfileDto>(`${this.base}/profile/${merchantId}`);
  }

  updateProfile(merchantId: number, req: UpdateProfileRequest): Observable<ProfileDto> {
    return this.http.put<ProfileDto>(`${this.base}/profile/${merchantId}`, req);
  }

  // ---- Trading ----

  buyRate(merchantId: number, req: RateRequest): Observable<RateResultDto> {
    return this.http.post<RateResultDto>(
      `${this.base}/trading/rates/buy`, req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  sellRate(merchantId: number, req: RateRequest): Observable<RateResultDto> {
    return this.http.post<RateResultDto>(
      `${this.base}/trading/rates/sell`, req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  buyCrypto(merchantId: number, req: BuyCryptoRequest): Observable<TradeResultDto> {
    return this.http.post<TradeResultDto>(
      `${this.base}/trading/buy`, req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  sellCrypto(merchantId: number, req: SellCryptoRequest): Observable<TradeResultDto> {
    return this.http.post<TradeResultDto>(
      `${this.base}/trading/sell`, req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  /** Business accounts owned by the signed-in merchant. Scoped server-side to the caller. */
  businesses(): Observable<BusinessAccountDto[]> {
    return this.http.get<BusinessAccountDto[]>(`${this.base}/profile/businesses`);
  }

  business(businessId: number | string): Observable<BusinessAccountDto> {
    return this.http.get<BusinessAccountDto>(`${this.base}/profile/businesses/${businessId}`);
  }

  createBusiness(req: CreateBusinessAccountRequest): Observable<BusinessAccountDto> {
    return this.http.post<BusinessAccountDto>(`${this.base}/profile/businesses`, req);
  }

  updateBusiness(businessId: number | string, req: CreateBusinessAccountRequest): Observable<BusinessAccountDto> {
    return this.http.put<BusinessAccountDto>(`${this.base}/profile/businesses/${businessId}`, req);
  }

  users(merchantShopId: number): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(`${this.base}/users-permissions`, {
      params: new HttpParams().set('merchantShopId', merchantShopId)
    });
  }

  addUser(merchantShopId: number, req: CreateUserRequest): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.base}/users-permissions`, req, {
      params: new HttpParams().set('merchantShopId', merchantShopId)
    });
  }

  updateUser(userId: number, req: CreateUserRequest): Observable<UserDto> {
    return this.http.put<UserDto>(`${this.base}/users-permissions/${userId}`, req);
  }

  /** Removes the user server-side; the table used to drop the row locally and it came back. */
  deleteUser(userId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/users-permissions/${userId}`);
  }

  /** The role catalogue and what each role grants — reference data, not a client-side list. */
  roles(): Observable<RoleDto[]> {
    return this.http.get<RoleDto[]>(`${this.base}/users-permissions/roles`);
  }

  // ---- Internal (merchant-to-merchant) transfers ----

  /** Resolve a transfer recipient by email. 404s when no merchant has that address. */
  merchantByEmail(email: string): Observable<MerchantLookupDto> {
    return this.http.get<MerchantLookupDto>(`${this.base}/wallet/merchant-by-email`, {
      params: new HttpParams().set('email', email)
    });
  }

  /** Preview an internal transfer. Internal transfers carry no processing fee. */
  calculateSendTo(merchantId: number, req: CalculateWithdrawRequest): Observable<WithdrawCalcDto> {
    return this.http.post<WithdrawCalcDto>(`${this.base}/wallet/calculate-send-to`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  /** Move funds to another Klidi merchant. */
  internalTransfer(merchantId: number, req: InternalTransferRequest): Observable<InternalTransferResultDto> {
    return this.http.post<InternalTransferResultDto>(`${this.base}/wallet/internal-transfer`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  /**
   * PDF receipt for a transaction, rendered on demand from the live record. Returned as a URL
   * because the browser downloads it directly.
   */
  /**
   * Transaction receipt PDF.
   *
   * Returned as a blob rather than a URL to open: a `window.open` of an API URL skips the HTTP
   * interceptor, so it arrives with no bearer token and renders an UNAUTHENTICATED error page.
   * Fetching it here sends the token like any other call. Pair with `saveBlobResponse`.
   */
  transactionReceiptPdf(publicId: string): Observable<HttpResponse<Blob>> {
    return this.downloadBlob(`${this.base}/transactions/${encodeURIComponent(publicId)}/receipt/pdf`);
  }

  cards(merchantId: number): Observable<CardDto[]> {
    return this.http.get<CardDto[]>(`${this.base}/cards/merchant/${merchantId}`);
  }

  orderCard(merchantId: number, req: OrderCardRequest): Observable<CardDto> {
    return this.http.post<CardDto>(`${this.base}/cards/merchant/${merchantId}/order`, req);
  }

  // ---- Deposit / Withdraw ----

  /**
   * Crypto currencies the deposit page offers. Comes from Scrypt's available currencies rather than
   * the merchant's accounts — the account, vault and address are created when an address is generated.
   */
  depositCurrencies(): Observable<DepositCurrencyDto[]> {
    return this.http.get<DepositCurrencyDto[]>(`${this.base}/crypto-wallets/deposit-currencies`);
  }

  generateDepositAddress(merchantId: number, currency: string): Observable<DepositAddressDto> {
    return this.http.post<DepositAddressDto>(
      `${this.base}/crypto-wallets/deposit-address`,
      { currency },
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  calculateWithdrawCrypto(merchantId: number, req: CalculateWithdrawRequest): Observable<WithdrawCalcDto> {
    return this.http.post<WithdrawCalcDto>(
      `${this.base}/wallet/calculate-withdraw-crypto`,
      req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  withdrawCrypto(merchantId: number, req: WithdrawCryptoRequest): Observable<WithdrawResultDto> {
    return this.http.post<WithdrawResultDto>(
      `${this.base}/wallet/withdraw/crypto`,
      req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  fiatWithdraw(merchantId: number, req: FiatWithdrawRequest): Observable<{ result: boolean; transactionId: string; status: string }> {
    return this.http.post<{ result: boolean; transactionId: string; status: string }>(
      `${this.base}/iban/fiat-withdraw`,
      req,
      { params: new HttpParams().set('merchantId', merchantId) }
    );
  }

  // ---- KYC ----

  kycStatus(merchantId: number): Observable<KycStatusDto> {
    return this.http.get<KycStatusDto>(`${this.base}/kyc/${merchantId}/status`);
  }

  kycUpdateIdDetails(merchantId: number, req: KycIdDetailsRequest): Observable<KycStatusDto> {
    return this.http.put<KycStatusDto>(`${this.base}/kyc/${merchantId}/id-details`, req);
  }

  /** Business (KYB) step — only valid for business accounts. */
  kycUpdateBusinessDetails(merchantId: number, req: KycBusinessDetailsRequest): Observable<KycStatusDto> {
    return this.http.put<KycStatusDto>(`${this.base}/kyc/${merchantId}/business-details`, req);
  }

  kycUpdateDocuments(merchantId: number, req: KycDocumentsRequest): Observable<KycStatusDto> {
    return this.http.put<KycStatusDto>(`${this.base}/kyc/${merchantId}/documents`, req);
  }

  kycSubmit(merchantId: number): Observable<KycStatusDto> {
    return this.http.post<KycStatusDto>(`${this.base}/kyc/${merchantId}/submit`, {});
  }

  kycStartShufti(merchantId: number): Observable<{ verificationUrl: string; reference: string; event: string; status: string | null }> {
    return this.http.post<{ verificationUrl: string; reference: string; event: string; status: string | null }>(
      `${this.base}/kyc/${merchantId}/shufti/start`, {});
  }

  // ---- Balance report ----

  balanceReport(merchantId: number): Observable<BalanceReportDto> {
    return this.http.get<BalanceReportDto>(`${this.base}/balance-report`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  balanceReportPdf(merchantId: number): Observable<HttpResponse<Blob>> {
    return this.downloadBlob(`${this.base}/balance-report/export/pdf?merchantId=${merchantId}`);
  }

  statementPdf(merchantId: number, from?: string, to?: string): Observable<HttpResponse<Blob>> {
    let url = `${this.base}/balance-report/statement/pdf?merchantId=${merchantId}`;
    if (from) url += `&from=${encodeURIComponent(from)}`;
    if (to) url += `&to=${encodeURIComponent(to)}`;
    return this.downloadBlob(url);
  }

  // ---- Fees ----

  defaultFees(): Observable<FeeDto[]> {
    return this.http.get<FeeDto[]>(`${this.base}/fees`);
  }

  merchantFees(merchantId: number): Observable<FeeDto[]> {
    return this.http.get<FeeDto[]>(`${this.base}/fees/merchants/${merchantId}`);
  }

  // ---- Affiliate ----

  affiliateTransactions(merchantId: number, take = 100): Observable<AffiliateTransactionDto[]> {
    return this.http.get<AffiliateTransactionDto[]>(`${this.base}/affiliate/transactions`, {
      params: new HttpParams().set('merchantId', merchantId).set('take', take)
    });
  }

  affiliateXls(merchantId: number): Observable<HttpResponse<Blob>> {
    return this.downloadBlob(`${this.base}/affiliate/transactions/export/xls?merchantId=${merchantId}`);
  }

  // ---- Payment methods ----

  paymentMethods(merchantId: number): Observable<PaymentMethodDto[]> {
    return this.http.get<PaymentMethodDto[]>(`${this.base}/payment-methods/merchant/${merchantId}`);
  }

  addBankMethod(merchantId: number, req: AddBankMethodRequest): Observable<PaymentMethodDto> {
    return this.http.post<PaymentMethodDto>(`${this.base}/payment-methods/bank`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  addCardMethod(merchantId: number, req: AddCardMethodRequest): Observable<PaymentMethodDto> {
    return this.http.post<PaymentMethodDto>(`${this.base}/payment-methods/card`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  removePaymentMethod(merchantId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/payment-methods/${id}`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  /**
   * Currencies this environment offers, with what each supports — the source of truth for every currency
   * picker. Read it through CurrencyCatalog, which loads it once.
   */
  currencies(): Observable<CurrencyDto[]> {
    return this.http.get<CurrencyDto[]>(`${this.base}/currencies`);
  }

  // ---- Shops ----

  shops(merchantId: number, search?: string): Observable<ShopDto[]> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    return this.http.get<ShopDto[]>(`${this.base}/shops/merchant/${merchantId}`, { params });
  }

  shop(shopId: number, merchantId: number): Observable<ShopDto> {
    return this.http.get<ShopDto>(`${this.base}/shops/${shopId}`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  createShop(merchantId: number, req: CreateShopRequest): Observable<ShopDto> {
    return this.http.post<ShopDto>(`${this.base}/shops`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  updateShop(shopId: number, merchantId: number, req: UpdateShopRequest): Observable<ShopDto> {
    return this.http.put<ShopDto>(`${this.base}/shops/${shopId}`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  shopOrders(shopId: number, merchantId: number, filter?: {
    search?: string; status?: string; dateFrom?: string; dateTo?: string; page?: number; pageSize?: number;
  }): Observable<PagedResult<ShopOrderDto>> {
    let params = new HttpParams().set('merchantId', merchantId);
    if (filter?.search) params = params.set('search', filter.search);
    if (filter?.status) params = params.set('status', filter.status);
    if (filter?.dateFrom) params = params.set('dateFrom', filter.dateFrom);
    if (filter?.dateTo) params = params.set('dateTo', filter.dateTo);
    if (filter?.page) params = params.set('page', filter.page);
    if (filter?.pageSize) params = params.set('pageSize', filter.pageSize);
    return this.http.get<PagedResult<ShopOrderDto>>(`${this.base}/shops/${shopId}/orders`, { params });
  }

  shopOrderDetails(shopId: number, orderId: number, merchantId: number): Observable<ShopOrderDetailDto> {
    return this.http.get<ShopOrderDetailDto>(`${this.base}/shops/${shopId}/orders/${orderId}`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  /** Receipt PDF for a completed order — see `transactionReceiptPdf` for why this is fetched, not linked. */
  shopOrderReceiptPdf(shopId: number, orderId: number, merchantId: number): Observable<HttpResponse<Blob>> {
    return this.downloadBlob(`${this.base}/shops/${shopId}/orders/${orderId}/receipt.pdf?merchantId=${merchantId}`);
  }

  createInvoice(shopId: number, merchantId: number, req: CreateInvoiceRequest): Observable<ShopOrderDto> {
    return this.http.post<ShopOrderDto>(`${this.base}/shops/${shopId}/invoices`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  // ---- API POS orders (across all merchant shops) ----

  apiPosOrders(merchantId: number, take = 100): Observable<ApiPosOrderDto[]> {
    return this.http.get<ApiPosOrderDto[]>(`${this.base}/orders`, {
      params: new HttpParams().set('merchantId', merchantId).set('take', take)
    });
  }

  // ---- Standing orders (Treasury > Activity) ----

  standingOrders(merchantId: number): Observable<StandingOrderDto[]> {
    return this.http.get<StandingOrderDto[]>(`${this.base}/standing-orders/merchant/${merchantId}`);
  }

  createStandingOrder(merchantId: number, req: CreateStandingOrderRequest): Observable<StandingOrderDto> {
    return this.http.post<StandingOrderDto>(`${this.base}/standing-orders/merchant/${merchantId}`, req);
  }

  updateStandingOrder(orderId: number, req: UpdateStandingOrderRequest): Observable<StandingOrderDto> {
    return this.http.put<StandingOrderDto>(`${this.base}/standing-orders/${orderId}`, req);
  }

  /** Cancels rather than deletes — the run history stays auditable. */
  cancelStandingOrder(orderId: number): Observable<{ result: boolean }> {
    return this.http.delete<{ result: boolean }>(`${this.base}/standing-orders/${orderId}`);
  }

  /** Execute now, out of schedule. Does not move the next run date. */
  runStandingOrder(orderId: number): Observable<StandingOrderRunResultDto> {
    return this.http.post<StandingOrderRunResultDto>(`${this.base}/standing-orders/${orderId}/run`, {});
  }

  // ---- Reports > Intelligence ----

  marketIndices(quote = 'EUR', days = 30): Observable<MarketIndicesDto> {
    return this.http.get<MarketIndicesDto>(`${this.base}/intelligence/market-indices`, {
      params: new HttpParams().set('quote', quote).set('days', days)
    });
  }

  anomalyAlerts(merchantId: number, days = 90): Observable<AnomalyAlertsDto> {
    return this.http.get<AnomalyAlertsDto>(`${this.base}/intelligence/anomalies`, {
      params: new HttpParams().set('merchantId', merchantId).set('days', days)
    });
  }

  // ---- Reports > Performance ----

  performance(merchantId: number, from?: string, to?: string): Observable<PerformanceDto> {
    let params = new HttpParams().set('merchantId', merchantId);
    if (from) params = params.set('from', from);
    if (to) params = params.set('to', to);
    return this.http.get<PerformanceDto>(`${this.base}/performance`, { params });
  }

  // ---- Settings > Notifications ----

  notificationPreferences(merchantId: number): Observable<NotificationPreferences> {
    return this.http.get<NotificationPreferences>(`${this.base}/profile/${merchantId}/notifications`);
  }

  updateNotificationPreferences(
    merchantId: number, prefs: NotificationPreferences
  ): Observable<{ result: boolean }> {
    return this.http.put<{ result: boolean }>(`${this.base}/profile/${merchantId}/notifications`, prefs);
  }

  // ---- Limit orders ----

  limitOrders(merchantId: number): Observable<LimitOrderDto[]> {
    return this.http.get<LimitOrderDto[]>(`${this.base}/limit-orders/merchant/${merchantId}`);
  }

  /** Reserves the funds as it places, so this 400s when the balance isn't there. */
  placeLimitOrder(merchantId: number, req: CreateLimitOrderRequest): Observable<LimitOrderDto> {
    return this.http.post<LimitOrderDto>(`${this.base}/limit-orders/merchant/${merchantId}`, req);
  }

  /** Cancels an open order and releases its hold. */
  cancelLimitOrder(orderId: number): Observable<{ result: boolean }> {
    return this.http.delete<{ result: boolean }>(`${this.base}/limit-orders/${orderId}`);
  }

  // ---- Notifications ----

  notifications(merchantId: number, take = 20, unreadOnly = false): Observable<NotificationFeedDto> {
    return this.http.get<NotificationFeedDto>(`${this.base}/notifications`, {
      params: new HttpParams()
        .set('merchantId', merchantId)
        .set('take', take)
        .set('unreadOnly', unreadOnly)
    });
  }

  /** Cheap enough for the header to poll — a count over an indexed predicate. */
  unreadNotificationCount(merchantId: number): Observable<UnreadCountDto> {
    return this.http.get<UnreadCountDto>(`${this.base}/notifications/unread-count`, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  markNotificationRead(id: number): Observable<{ result: boolean }> {
    return this.http.post<{ result: boolean }>(`${this.base}/notifications/${id}/read`, {});
  }

  markAllNotificationsRead(merchantId: number): Observable<{ result: boolean; updated: number }> {
    return this.http.post<{ result: boolean; updated: number }>(
      `${this.base}/notifications/read-all`, {},
      { params: new HttpParams().set('merchantId', merchantId) });
  }

  // ---- Web push ----

  pushConfig(): Observable<PushConfigDto> {
    return this.http.get<PushConfigDto>(`${this.base}/push/config`);
  }

  subscribeToPush(merchantId: number, req: PushSubscribeRequest): Observable<{ result: boolean }> {
    return this.http.post<{ result: boolean }>(`${this.base}/push/subscribe`, req, {
      params: new HttpParams().set('merchantId', merchantId)
    });
  }

  unsubscribeFromPush(endpoint: string): Observable<{ result: boolean }> {
    return this.http.post<{ result: boolean }>(`${this.base}/push/unsubscribe`, { endpoint });
  }
}
